import { isSelfStatusOrigin } from '../../statuses/status-origin';
import { intval, strval, intdiv, strcasecmp, is_numeric, empty } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
import { slot, values, type Arr } from './access';
import { catalogRows, catalogId } from './catalog';
export interface CraftFlowDeps {
    check_uuid(id: string): boolean;
    fetch_statuses(filters: Arr): Arr;
    campaign_time_to_seconds(campaign: Arr): unknown;
    caster_skill_entry(caster: Arr, skill: string): unknown;
}
/** PHP: ft_crafting_wip_salvage_rows (includes/functions/janus/statuses/crafting_wip_flow.php:97) */
export function ftCraftingWipSalvageRows(manifest: Arr): Arr {
    const rows: Arr[] = [];
    for (const entry of values(manifest)) {
        const qty = Number(intval(slot(entry, 'qty') ?? 0)), refund = qty - Math.max(1, Number(intdiv(qty, 2)));
        if (refund > 0)
            rows.push({ item_uuid: strval(slot(entry, 'item_uuid')), template_id: slot(entry, 'template_id'), qty: refund });
    }
    return rows;
}
/** PHP: ft_crafting_wip_tracker_row_for_inventory (includes/functions/janus/statuses/crafting_wip_flow.php:117) */
export function ftCraftingWipTrackerRowForInventory(id: string, env: PortEnv<CraftFlowDeps>): Arr | null {
    if (!env.deps.check_uuid(id))
        return null;
    for (const row of values(env.deps.fetch_statuses({ status_target_id: id, status_item_id: catalogId('StatusEffect', 'crafting_progress', env), status_deleted: 0 })))
        return row as Arr;
    return null;
}
/** PHP: ft_crafting_skill_variable_name (includes/functions/janus/statuses/crafting_wip_flow.php:138) */
export function ftCraftingSkillVariableName(uuid: string): string | null {
    for (const skill of catalogRows('Skill').values())
        if (strcasecmp(strval(slot(skill, 'item_id') ?? ''), uuid) === 0)
            return strval(slot(skill, 'variable_name'));
    return null;
}
/** PHP: ft_crafting_skill_tier_for_creature (includes/functions/janus/statuses/crafting_wip_flow.php:149) */
export function ftCraftingSkillTierForCreature(creature: Arr, uuid: string, env: PortEnv<CraftFlowDeps>): number {
    const variable = ftCraftingSkillVariableName(uuid);
    if (variable === null)
        return 0;
    const tier = slot(env.deps.caster_skill_entry(creature, variable), 'tier_level') ?? 0;
    return is_numeric(tier) ? Math.max(0, Number(intval(tier))) : 0;
}
import { collectWriteIntents as b3collectWriteIntents, type PortWithWrites as b3PortWithWrites } from './support-b3';
import { strtolower as b3strtolower } from './support-b3';
import { catalogId as b3catalogId, type Row as b3Row, type PortEnv as b3PortEnv, type PhpRef as b3PhpRef, get as b3get, empty as b3empty, isArray as b3isArray, values as b3values, intval as b3intval, strval as b3strval, is_numeric as b3is_numeric, intdiv as b3intdiv, SKILLS as b3SKILLS } from './support-b3';
import { ftCraftingProgressPayload as b3ftCraftingProgressPayload, ftCraftingWorkerPayloadParse as b3ftCraftingWorkerPayloadParse, ftCraftingWorkerPayloadEncode as b3ftCraftingWorkerPayloadEncode } from './crafting_wip_helpers';
interface B3Deps {
    check_uuid(id: string): boolean;
    fetch_statuses(filter: b3Row): unknown;
    'StatusEffect::getId'(variableName: string): string;
    campaign_time_to_seconds(campaign: b3Row): unknown;
    caster_skill_entry(creature: b3Row, skill: string): unknown;
    query_d_full_item_arr_by_encounter_arr_reference(encounter: b3Row, campaign: b3Row | null, id: string): unknown;
    determine_item_type(id: string): unknown;
    query_encounter_creature_arr(id: string): unknown;
    get_encounter_creature_permissions(row: b3PhpRef<b3Row>): unknown;
    query_d_item_arr(id: string): unknown;
    item_field(row: b3Row, key: string): unknown;
    get_status_arr(id: string): unknown;
    apply_status_effect(connected: string, caster: b3Row, target: b3PhpRef<b3Row>, targets: string, affected: b3PhpRef<b3Row>, origin: unknown, item: string, ends: number, rollId: string, log: boolean, message: string, private_: number, overlay: null, aoeSource: number, opposing: number, paused: null, hasDuration: boolean, bypass: boolean, campaign: b3Row): unknown;
    update_status_effect_stats(row: unknown, updates: b3Row, affected: b3PhpRef<b3Row>): unknown;
    is_status_effect_permanent(row: unknown): boolean;
    query_full_item_arr(id: string): unknown;
    ft_crafting_wip_finish(caster: b3PhpRef<b3Row>, affected: b3PhpRef<b3Row>, recipe: b3Row, recipeId: string, rollId: string, trackerId: string): unknown;
    ft_skip_server_rule(reason: string, worker: b3Row, rollId: string, encounter: b3Row | null, campaign: b3Row | null): unknown;
}
/** PHP: ft_crafting_wip_campaign_now_seconds (includes/functions/janus/statuses/crafting_wip_flow.php:133) */
export function ftCraftingWipCampaignNowSeconds(campaign: b3Row, env: b3PortEnv<B3Deps>): number { return Math.max(0, Number(b3intval(env.deps.campaign_time_to_seconds(campaign)))); }
/** PHP: ft_crafting_wip_resolve_context_item (includes/functions/janus/statuses/crafting_wip_flow.php:164) */
export function ftCraftingWipResolveContextItem(id: string, encounter: b3Row | null = null, campaign: b3Row | null = null, env: b3PortEnv<B3Deps>): b3Row | null {
    if (!env.deps.check_uuid(id))
        return null;
    if (b3isArray(encounter) && !b3empty(encounter)) {
        const row = env.deps.query_d_full_item_arr_by_encounter_arr_reference(encounter, campaign, id);
        if (b3isArray(row) && !b3empty(row))
            return row;
    }
    if (env.deps.determine_item_type(id) === 'encounter_creature') {
        const row = env.deps.query_encounter_creature_arr(id);
        if (b3isArray(row) && !b3empty(row)) {
            const ref = { value: row };
            env.deps.get_encounter_creature_permissions(ref);
            return ref.value;
        }
    }
    const row = env.deps.query_d_item_arr(id);
    return b3isArray(row) && !b3empty(row) ? row : null;
}
/** PHP: ft_crafting_wip_add_worker (includes/functions/janus/statuses/crafting_wip_flow.php:202) */
export function ftCraftingWipAddWorker(worker: b3PhpRef<b3Row>, tracker: b3Row, campaign: b3Row, affected: b3PhpRef<b3Row>, rollId: string, _authorityEncounterId: string | null = null, env: b3PortEnv<B3Deps>): string | null | b3PortWithWrites<string | null> {
    return b3collectWriteIntents(env, env => {
        const id = b3strval(b3get(tracker, 'status_id') ?? ''), payload = b3ftCraftingProgressPayload(tracker, env);
        const workerId = b3strval(b3get(worker.value, 'encounter_creatures', 'encounter_creature_id') ?? env.deps.item_field(worker.value, 'item_id') ?? '');
        if (!env.deps.check_uuid(id) || payload === null || !env.deps.check_uuid(workerId))
            return null;
        const fresh = env.deps.get_status_arr(id), freshPayload = !b3empty(fresh) ? b3ftCraftingProgressPayload(fresh as b3Row, env) : null;
        if (b3empty(fresh) || !b3empty(b3get(fresh, 'status_deleted')) || freshPayload === null || freshPayload.state !== 'active')
            return null;
        env.deps.apply_status_effect(id, worker.value, worker, workerId, affected, b3get(tracker, 'status_origin_id') ?? '', b3catalogId('StatusEffect', 'crafting', env), 0, rollId, true, 'status_effect_applied', 0, null, 0, 0, null, false, false, campaign);
        const workerRow = b3values(env.deps.fetch_statuses({ status_target_id: workerId, status_item_id: b3catalogId('StatusEffect', 'crafting', env), status_connected_id: id, status_deleted: 0 }))[0] ?? null;
        if (workerRow === null || !env.deps.check_uuid(b3strval(b3get(workerRow, 'status_id') ?? '')))
            return null;
        let now = ftCraftingWipCampaignNowSeconds(campaign, env);
        const existing = b3ftCraftingWorkerPayloadParse(b3get(workerRow, 'status_casting_targets_arr') ?? null, env);
        if (existing !== null && b3strtolower(existing.wip_inventory_id) === b3strtolower(payload.target_id))
            now = Math.max(now, existing.last_settled_seconds);
        env.deps.update_status_effect_stats(workerRow, { status_casting_targets_arr: b3ftCraftingWorkerPayloadEncode({ target_id: workerId, wip_inventory_id: payload.target_id, last_settled_seconds: now }, env) }, affected);
        const after = env.deps.get_status_arr(id);
        if (b3empty(after) || !b3empty(b3get(after, 'status_deleted'))) {
            env.deps.update_status_effect_stats(workerRow, { status_deleted: 1 }, affected);
            return null;
        }
        const siblings: string[] = [];
        for (const row of b3values(env.deps.fetch_statuses({ status_target_id: workerId, status_item_id: b3catalogId('StatusEffect', 'crafting', env), status_deleted: 0 })))
            if (env.deps.is_status_effect_permanent(row) && b3ftCraftingWorkerPayloadParse(b3get(row, 'status_casting_targets_arr') ?? null, env) !== null)
                siblings.push(b3strtolower(b3strval(b3get(row, 'status_id'))));
        if (siblings.length > 1) {
            siblings.sort();
            if (siblings[0] !== b3strtolower(b3strval(b3get(workerRow, 'status_id')))) {
                env.deps.update_status_effect_stats(workerRow, { status_deleted: 1 }, affected);
                return null;
            }
        }
        return b3strval(b3get(workerRow, 'status_id'));
    });
}
/** PHP: ft_crafting_wip_invoke_completion_if_pending (includes/functions/janus/statuses/crafting_wip_flow.php:471) */
export function ftCraftingWipInvokeCompletionIfPending(settle: b3Row | null, tracker: b3Row, encounter: b3PhpRef<b3Row | null>, campaign: b3Row | null, affected: b3PhpRef<b3Row>, rollId: string, _authorityEncounterId: string | null = null, env: b3PortEnv<B3Deps>): null | b3PortWithWrites<null> {
    return b3collectWriteIntents(env, env => {
        if (!b3isArray(settle) || b3empty(b3get(settle, 'completion_pending')))
            return null;
        const id = b3strval(b3get(tracker, 'status_id') ?? ''), recipeId = b3strval(b3get(tracker, 'status_origin_id') ?? ''), wipId = b3strval(b3get(settle, 'wip_inventory_id') ?? '');
        if (!env.deps.check_uuid(id) || !env.deps.check_uuid(recipeId) || !env.deps.check_uuid(wipId))
            return null;
        for (const workerId of b3values(b3get(settle, 'resolving_worker_status_ids') ?? [])) {
            const row = env.deps.get_status_arr(b3strval(workerId));
            if (b3empty(row))
                continue;
            const targetId = b3strval(b3get(row, 'status_target_id') ?? '');
            if (!env.deps.check_uuid(targetId))
                continue;
            const target = ftCraftingWipResolveContextItem(targetId, encounter.value, campaign, env);
            if (!b3isArray(target) || b3empty(target))
                continue;
            const recipe = isSelfStatusOrigin(id, recipeId) ? null : env.deps.query_full_item_arr(recipeId);
            if (b3isArray(recipe) && !b3empty(recipe))
                env.deps.ft_crafting_wip_finish({ value: target }, affected, recipe, recipeId, rollId, id);
            const after = env.deps.get_status_arr(id);
            if (b3empty(after) || !b3empty(b3get(after, 'status_deleted')))
                return null;
            env.deps.ft_skip_server_rule('server-crafting:pending-recipe-completion:not-ported', target, rollId, encounter.value, campaign);
            return null;
        }
        return null;
    });
}
